import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import api from 'services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? '/api' 
    : 'http://localhost:5000/api'
);

interface MonthlyUsage {
  month: string;
  count: number;
}

function getAxiosResponseMessage(err: unknown): string | undefined {
  if (!axios.isAxiosError(err) || err.response?.data == null) return undefined;
  const data = err.response.data;
  if (typeof data === 'object' && data !== null) {
    const msg = Reflect.get(data, 'message');
    return typeof msg === 'string' ? msg : undefined;
  }
  return undefined;
}

function groupUsageByMonth(data: any[]): MonthlyUsage[] {
  const monthMap: { [key: string]: number } = {};

  data.forEach(item => {
    const date = new Date(item.created_at);
    const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

    if (!monthMap[monthName]) {
      monthMap[monthName] = 0;
    }
    if (item.usage_triage) monthMap[monthName]++;
    if (item.usage_description) monthMap[monthName]++;
    if (item.usage_formatting) monthMap[monthName]++;
    if (item.usage_validation) monthMap[monthName]++;
  });

  return Object.entries(monthMap)
    .map(([month, count]) => ({ month, count }))
    .sort((a, b) => {
      const dateA = new Date(a.month);
      const dateB = new Date(b.month);
      return dateB.getTime() - dateA.getTime();
    });
}

const ClientUsageStats: React.FC = () => {
  const { t } = useTranslation();
  const [usageData, setUsageData] = useState<MonthlyUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(false);

  const fetchUsageStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await api.get(`${API_BASE_URL}/usage`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const grouped = groupUsageByMonth(response.data);

      if (!isMountedRef.current) return;
      setUsageData(grouped);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching usage stats:', err);
      if (!isMountedRef.current) return;
      const message = getAxiosResponseMessage(err);
      setError(message || t('clientUsageStats.failedToFetch'));
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchUsageStats();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchUsageStats]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('clientUsageStats.title')}</h3>
        <div className="text-gray-500 dark:text-gray-400">{t('clientUsageStats.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('clientUsageStats.title')}</h3>
        <div className="text-red-500 dark:text-red-400">{error}</div>
      </div>
    );
  }

  const totalUsage = usageData.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('clientUsageStats.title')}</h3>
      
      <div className="mb-6">
        <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{totalUsage}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400">{t('clientUsageStats.totalApiCalls')}</div>
      </div>

      {usageData.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">{t('clientUsageStats.noData')}</p>
      ) : (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('clientUsageStats.monthlyUsage')}</h4>
          {usageData.map((item, index) => (
            <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-gray-700 dark:text-gray-300">{item.month}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{item.count} {t('clientUsageStats.calls')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientUsageStats;
